import { getTeamsSearchOptions } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { Label, Select, Spinner, Switch } from "@win-reality/react-ui";
import { useEffect, useMemo } from "react";

import { HorizontalMenu } from "@/components/ui/HorizontalMenu";
import { useAppContext } from "@/hooks/use-app-context";
import { useDashboardStore } from "@/stores/dashboard";

type RosterSelectionProps = {
  selectedTeamId: string | null;
  setSelectedTeamId: (team: string | null) => void;
};

const TAB_OPTIONS = [
  { value: "hitting", label: "Hitting" },
  { value: "pitching", label: "Pitchers" },
] as const;

const YEAR_OPTIONS_COUNT = 5;

export const RosterSelection = ({
  selectedTeamId,
  setSelectedTeamId,
}: RosterSelectionProps) => {
  const { orgId } = useAppContext();
  const {
    includePractice,
    filter,
    selectedYear,
    setIncludePractice,
    setFilter,
    setSelectedYear,
  } = useDashboardStore();

  const { data, isLoading } = useQuery({
    ...getTeamsSearchOptions({
      query: {
        visibilityGroup: orgId!,
      },
    }),
    enabled: !!orgId,
  });

  const teams = useMemo(
    () =>
      data?.teams?.map((team) => ({
        label: team.known_names?.[0] ?? "",
        value: team.external_ids?.[0] ?? "",
      })) || [],
    [data],
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: YEAR_OPTIONS_COUNT }, (_, i) => ({
      label: (currentYear - i).toString(),
      value: (currentYear - i).toString(),
    }));
  }, []);

  useEffect(() => {
    if (teams.length > 0 && !isLoading) {
      setSelectedTeamId(teams[0].value || null);
    }

    if (teams.length === 0 && !isLoading) {
      setSelectedTeamId(null);
    }
  }, [teams, isLoading, setSelectedTeamId]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter as "hitting" | "pitching");
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-end gap-6">
        <div className="min-w-[280px] [&_input]:border-border-input [&_input]:pr-10">
          <Label className="mb-2 text-sm">Roster</Label>
          {isLoading ? (
            <div className="flex items-center h-10">
              <Spinner />
            </div>
          ) : (
            <Select
              items={teams}
              value={selectedTeamId ?? undefined}
              onValueChange={(value) => setSelectedTeamId(value)}
              placeholder="Select roster"
            />
          )}
        </div>

        <div className="min-w-[120px] [&_input]:border-border-input [&_input]:pr-10">
          <Label className="mb-2 text-sm">Year</Label>
          <Select
            items={yearOptions}
            value={selectedYear.toString()}
            onValueChange={(value) => setSelectedYear(parseInt(value, 10))}
          />
        </div>

        <div className="flex items-center gap-2 h-10">
          <Switch
            checked={includePractice}
            onCheckedChange={setIncludePractice}
          />
          <Label className="mb-0 text-sm text-text-light">
            Include Practice
          </Label>
        </div>
      </div>

      <HorizontalMenu
        items={TAB_OPTIONS.map((option) => ({
          label: option.label,
          active: filter === option.value,
          onClick: () => handleFilterChange(option.value),
        }))}
      />
    </div>
  );
};
